import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, TextField, Box } from "@mui/material";
import { IconClose } from "../../Icons/Icons";
import { colors, transitions } from "../../../styles/theme";
import ImagePlaceholder from "../../../assets/images/form_image_placeholder.png";
import { Description } from "../components/Description/Description";
import { Title } from "../components/TItle/Title";
import { TextInput } from "../components/TextInput/TextInput";
import { LabeledCheckbox } from "../components/LabeledCheckbox/LabeledCheckbox";
import { SubmitButton } from "../components/SubmitButton/SubmitButton";
import { BasicLink } from "../components/BasicLink/BasicLink";
import { GradientFrame } from "../../GradientFrame/GradientFrame";
import { useForm } from "../hooks";
import { FormProps } from "../types";
import { getFluidsByKeysFunc } from "../../../utils/fuilds";
import { useResponsiveScaler } from "../../../lib/ResponsiveScaler";
import { BannerContainer } from "./ReceivePdfForm.styled";
import { BREAKPOINT_MOBILE } from "../../../constants";
import { PhoneNumberInput } from "../components/PhoneNumberInput/PhoneNumberInput";

export type ReceivePdfFormProps = FormProps & {
  banner?: string;
};

export const ReceivePdfForm: React.FC<ReceivePdfFormProps> = (props) => {
  const { open, onClose, onSubmit, disabled, banner } = props;
  const { values, errors, handleChange, handleBlur, handleSubmit, showError } =
    useForm({ onSubmit });
  const { rpx } = useResponsiveScaler();
  const [bannerImage, setBannerImage] = useState();

  useEffect(() => {
    if (banner) {
      /* @ts-ignore */
      setBannerImage(banner);
      // bannerImage = props.banner;
    } else {
      setBannerImage(undefined);
    }
  }, [props.banner]);

  useEffect(() => {
    if (!open) {
      // bannerImage = undefined;
      setBannerImage(undefined);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <GradientFrame $type={"top"}>
          <Box
            sx={(theme) => ({
              display: "flex",
              background: colors.background.v1,
              flexDirection: {
                xs: "column",
                [BREAKPOINT_MOBILE]: "row",
              },
              borderRadius: "15px",
              ...rpx({
                gap: "54px",
                padding: "60px",
              }),
            })}
          >
            {/* Left column */}
            <Box
              sx={{
                flex: { xs: "0 0 auto", [BREAKPOINT_MOBILE]: "1 1 60%" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: "20px",
                  ...rpx(
                    {
                      marginBottom: "50px",
                    },
                    {
                      mobile: {
                        marginBottom: "10px",
                        columnGap: "10px",
                        minHeight: "100px",
                      },
                    },
                  ),
                }}
              >
                <Box>
                  <Title>Receive Your configuration</Title>
                  <Description>
                    Enter your details and we’ll send your personalized SAY
                    configuration as a PDF summary.
                  </Description>
                </Box>
                <BannerContainer
                  sx={{
                    display: "none",
                    ...(Boolean(bannerImage) && {
                      backgroundImage: `url(${bannerImage})`,
                      border: "none",
                      "&::before": {
                        visibility: "hidden !important",
                      },
                    }),
                    ...rpx(
                      {},
                      {
                        mobile: { display: "block" },
                      },
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "26px",
                  ...rpx(
                    {},
                    {
                      mobile: {
                        gap: "10px",
                      },
                    },
                  ),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    columnGap: "26px",
                    ...rpx(
                      {},
                      {
                        mobile: {
                          columnGap: "10px",
                        },
                      },
                    ),
                  }}
                >
                  <TextInput
                    containerProps={{ width: "50%" }}
                    label="First name*"
                    variant="outlined"
                    fullWidth
                    value={values.first_name}
                    // @ts-ignore
                    onChange={handleChange("first_name")}
                    onBlur={handleBlur("first_name")}
                    error={showError("first_name")}
                    helperText={
                      showError("first_name") ? errors.first_name : " "
                    }
                  />
                  <TextInput
                    containerProps={{ width: "50%" }}
                    label="Last name*"
                    variant="outlined"
                    fullWidth
                    value={values.last_name}
                    // @ts-ignore
                    onChange={handleChange("last_name")}
                    onBlur={handleBlur("last_name")}
                    error={showError("last_name")}
                    helperText={showError("last_name") ? errors.last_name : " "}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "26px",
                    ...rpx(
                      {},
                      {
                        mobile: {
                          gap: "10px",
                        },
                      },
                    ),
                  }}
                >
                  <TextInput
                    // containerProps={{ sx: { flexGrow: 2 } }}
                    label="Email*"
                    variant="outlined"
                    fullWidth
                    value={values.email}
                    // @ts-ignore
                    onChange={handleChange("email")}
                    onBlur={handleBlur("email")}
                    error={showError("email")}
                    helperText={showError("email") ? errors.email : " "}
                  />
                  <PhoneNumberInput
                    value={values.phone}
                    onChange={(val) =>
                      handleChange("phone")({ target: { value: val } })
                    }
                    error={showError("phone")}
                    helperText={showError("phone") ? errors.phone : " "}
                  />
                  <TextField
                    label="Message (optional)"
                    multiline
                    rows={5}
                    fullWidth
                    name="message"
                    value={values.message ?? ""}
                    onChange={handleChange("message")}
                    onBlur={handleBlur("message")}
                  />
                </Box>
              </Box>
            </Box>

            {/* Right column */}
            <Box
              sx={{
                flex: { xs: "0 0 auto", md: "1 1 40%" },
                display: "grid",
                // gridTemplateRows: { xs: "150px auto", md: "1fr auto" },
                gridTemplateRows: { xs: "auto", md: "1fr auto" },
                gap: 0,
              }}
            >
              <BannerContainer
                sx={{
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: "15px",
                  ...(Boolean(bannerImage) && {
                    backgroundImage: `url(${bannerImage})`,
                    border: "none",
                    "&::before": {
                      visibility: "hidden !important",
                    },
                  }),
                  ...rpx(
                    {},
                    {
                      mobile: { display: "none" },
                    },
                  ),
                }}
              />

              <Box
                sx={{
                  ...rpx(
                    { marginTop: "30px" },
                    {
                      mobile: { marginTop: "10px" },
                    },
                  ),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: { xs: "10px", lg: "20px" },
                  }}
                >
                  <LabeledCheckbox
                    value={values.accepted}
                    name={"accepted"}
                    text={"Accept Privacy Policy*"}
                    onChange={handleChange("accepted")}
                    onBlur={handleBlur("accepted")}
                    error={showError("accepted")}
                  />
                  <BasicLink
                    href={"https://saycarbonyachts.com/privacy-policy"}
                    target={"_blank"}
                    typographyProps={{
                      variant: "v2",
                    }}
                  >
                    View Privacy Policy
                  </BasicLink>
                </Box>

                <LabeledCheckbox
                  value={values.signed_up}
                  name={"signed_up"}
                  text={"Sign up for SAY Carbon Yachts Newsletter."}
                  onChange={handleChange("signed_up")}
                  onBlur={handleBlur("signed_up")}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "55px",
                  }}
                >
                  <SubmitButton onClick={handleSubmit} disabled={disabled}>
                    Send PDF
                  </SubmitButton>
                  <IconClose
                    sx={{
                      color: colors.text.v2,
                      cursor: "pointer",
                      alignSelf: "end",
                      transition: transitions.join("color"),
                      "&:hover": { color: colors.text.v3 },
                    }}
                    onClick={onClose}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </GradientFrame>
      </DialogContent>
    </Dialog>
  );
};
